import React, { useImperativeHandle, useState } from 'react';
import { Input, Table as ReactstrapTable } from 'reactstrap';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { IconProp } from '~components/icon';

import { getDefaultRenderEditor, getDefaultRenderReadOnly } from './columnBuilder';
import DeleteAction from './DeleteAction';
import EditAction from './EditAction';
import { buildSortState, getNextSortDirection, getSortDirection } from './sorter';
import TableEmpty from './TableEmpty';
import TableFooter from './TableFooter';
import TableHeader from './TableHeader';
import TableLoading from './TableLoading';
import TablePagination from './TablePagination';
import { Column, Context, EditorContext, Row, SortState } from './types';
import { buildTableValidationSchema, extractInvalidCells } from './validation';

export interface TableViewHandle {
  validate: () => Promise<boolean>;
}

interface ComponentProps {
  id: string;
  data: Row[];
  columns: Column[];
  isLoading?: boolean;
  onChange?: (data: Row[]) => void;

  // parent component data provided to onChange / deriveValue functions
  context?: Context;

  onSaveRow?: (row: Row) => void;

  // callback fired when a row enters or exits edit mode
  onEditRow?: (row: Row, isEditing: boolean) => void;

  // callback fired on row population to check whether or not a row is allowed to be deleted
  canDeleteRow?: ((row: Row) => boolean) | boolean;

  // callback fired to disable all functionality in a row, except actions
  isRowDisabled?: ((row: Row) => boolean) | boolean;

  // callback fired to not validate any cells in a row
  canRowValidate?: ((row: Row) => boolean) | boolean;

  // Delete OVERRIDE function - if provided, this will be called instead of internal delete handling
  onDeleteRow?: (rowId: string | number) => void;

  // onChange OVERRIDE function - if provided, this will be called instead of usual onChange handler
  onChangeRow?: (row: Row) => void;

  // Prevent add/delete/edit functionality
  readonly?: boolean;

  // Props for customising when no data is available
  emptyIcon?: IconProp;
  emptyMessage?: string;

  // Force only editing rows
  editonly?: boolean;

  // Add compact mode for slimmer design (styling, mostly)
  compactView?: boolean;

  // Add toggle for bordered view
  bordered?: boolean;

  // Add toggle for striped rows
  striped?: boolean;

  // row select flag & callback to parent on selection
  canSelect?: boolean;
  selectedIds?: string[];
  onRowSelectionChange?: (selectedIds: string[]) => void;

  // Add toggle for pagination and loading more rows
  showAddRow?: boolean;
  allowPagination?: boolean;
  totalCount?: number;
  onLoadMore?: () => void;

  // Add props for sorting
  allowSort?: boolean;
  initialSortState?: SortState;
  onSort?: (params: SortState) => void;

  // Add props for page-based pagination
  paginationType?: 'loadmore' | 'pages';
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  showCount?: boolean;
  showPageSizeOptions?: boolean;
}

const TableView = React.forwardRef<TableViewHandle, ComponentProps>(
  (
    {
      id,
      data,
      columns,
      isLoading = false,
      onChange,
      context,
      onSaveRow,
      onEditRow,
      canDeleteRow,
      onDeleteRow,
      onChangeRow,
      readonly,
      emptyIcon,
      emptyMessage,
      editonly = false,
      compactView = false,
      bordered = false,
      striped = false,
      canSelect = false,
      selectedIds = [],
      onRowSelectionChange,
      isRowDisabled = false,
      canRowValidate = true,
      showAddRow = true,
      allowPagination = false,
      totalCount = 0,
      onLoadMore,
      allowSort = false,
      initialSortState = [],
      onSort,
      paginationType = 'loadmore',
      currentPage = 1,
      pageSize = 30,
      onPageChange,
      onPageSizeChange,
      pageSizeOptions,
      showCount = false,
      showPageSizeOptions = false,
    },
    ref,
  ) => {
    const [editingRows, setEditingRows] = useState<Set<string | number>>(new Set());
    const [invalidCells, setInvalidCells] = useState<Set<string>>(new Set());
    const [sortState, setSortState] = useState<SortState>(initialSortState);

    const generateRowId = () => uuidv4();

    const handleAddRow = () => {
      if (readonly) return;

      const newRow = { id: generateRowId() };
      onChange && onChange([...data, newRow]);
      toggleEditRow(newRow.id);
    };

    const handleDeleteRow = (rowId: string | number) => {
      if (readonly) return;

      if (onDeleteRow) {
        onDeleteRow(rowId);
        return;
      }

      const updatedData = data.filter((row) => row.id !== rowId);

      // Clean up invalid cells / failed validation for deleted row
      const rowIndex = data.findIndex((row) => row.id === rowId);
      if (rowIndex !== -1) {
        setInvalidCells((prev) => {
          const updated = new Set<string>();
          for (const cell of prev) {
            const [indexPart, ...rest] = cell.split('.');
            const index = Number(indexPart);
            if (Number.isNaN(index)) continue;
            if (index === rowIndex) continue;
            const newIndex = index > rowIndex ? index - 1 : index;
            updated.add([newIndex, ...rest].join('.'));
          }
          return updated;
        });
      }

      onChange && onChange(updatedData);
    };

    const handleSelectRow = (rowIds: string[], isBulkOperation = false) => {
      let newSelectedIds: string[];

      if (isBulkOperation) {
        // Bulk operation from header checkbox (select all or deselect all)
        newSelectedIds = rowIds;
      } else {
        // Toggle individual rows
        newSelectedIds = [...selectedIds];
        rowIds.forEach((rowId) => {
          const rowIdString = String(rowId);
          if (selectedIds.includes(rowIdString)) {
            newSelectedIds = newSelectedIds.filter((id) => id !== rowIdString);
          } else {
            newSelectedIds.push(rowIdString);
          }
        });
      }

      onRowSelectionChange && onRowSelectionChange(newSelectedIds);
    };

    const handleCellChange = async (rowId: string | number, key: string, value: any) => {
      const updatedRows = [...data];
      const rowIndex = updatedRows.findIndex((row) => row.id === rowId);
      if (rowIndex === -1) return;

      const currentRow = updatedRows[rowIndex];
      let newRow = { ...currentRow, [key]: value };

      const column = columns.find((col) => col.id === key);

      // optional context passed from parent for use in onChange / deriveValue
      if (column?.onChange) {
        try {
          const nextRows = [...data];
          nextRows[rowIndex] = newRow;

          const enhancedContext = { ...(context || {}), getAllRows: () => nextRows };
          const extra = await column.onChange(newRow, enhancedContext, key);
          if (extra && typeof extra === 'object') {
            if (extra._meta) {
              // store _meta separately so it's not treated as a real column
              newRow._meta = { ...(newRow._meta || {}), ...extra._meta };
              delete extra._meta;
            }
            newRow = { ...newRow, ...extra };
            // keep nextRows in sync for any subsequent onChange / deriveValue calls
            nextRows[rowIndex] = newRow;
          }
        } catch (err) {
          console.error(`Error in onChange for column "${key}":`, err);
        }
      }

      for (const col of columns) {
        if (col.deriveValue) {
          try {
            newRow[col.id] = col.deriveValue(newRow, context);
          } catch (err) {
            console.error(`Error in deriveValue for column "${col.id}":`, err);
          }
        }
      }

      updatedRows[rowIndex] = newRow;
      if (onChangeRow) {
        // only return the changed row if onChangeRow override is provided
        onChangeRow(newRow);
      } else {
        // default behavior: call onChange with entire updated rows array
        onChange && onChange(updatedRows);
      }
    };

    const handleSort = (columnId: string) => {
      const nextDirection = getNextSortDirection(getSortDirection(sortState, columnId));
      const newSortState = buildSortState({ id: columnId, direction: nextDirection });
      setSortState(newSortState);
      onSort?.(newSortState);
    };

    const toggleEditRow = (rowId: string | number) => {
      if (readonly) return;

      setEditingRows((prev) => {
        const copy = new Set(prev);
        const isCurrentlyEditing = copy.has(rowId);

        if (isCurrentlyEditing) {
          copy.delete(rowId);
        } else {
          copy.add(rowId);
        }

        // Fire optional onEditRow callback if provided
        const row = data.find((r) => r.id === rowId);
        if (row && onEditRow) {
          onEditRow(row, !isCurrentlyEditing);
        }

        return copy;
      });
    };

    const validate = async () => {
      const schema = buildTableValidationSchema(columns);

      const shouldValidate = (row: Row) => {
        if (typeof canRowValidate === 'function') return canRowValidate(row);
        if (typeof canRowValidate === 'boolean') return canRowValidate;
        return true;
      };

      // Keep original cell positions by filtering with index tracking
      const rowsWithIndex = data
        .map((row, index) => ({ row, originalIndex: index }))
        .filter((item) => shouldValidate(item.row));

      const rowsToValidate = rowsWithIndex.map((item) => item.row);

      try {
        await schema.validate(rowsToValidate, { abortEarly: false });
        setInvalidCells(new Set());
        return true;
      } catch (err: any) {
        if (err instanceof Yup.ValidationError) {
          const invalid = extractInvalidCells(err);
          // Map back to correct cell positions
          const remapped = new Set<string>(
            Array.from(invalid).map((cell) => {
              const [index, ...rest] = cell.split('.');
              const filteredIndex = Number(index);
              if (!Number.isNaN(filteredIndex) && rowsWithIndex[filteredIndex]) {
                return `${rowsWithIndex[filteredIndex].originalIndex}.${rest.join('.')}`;
              }
              return cell;
            }),
          );
          setInvalidCells(remapped);
        }
        return false;
      }
    };

    // expose validate function to parent via ref
    useImperativeHandle(ref, () => ({
      validate,
    }));

    const isRowEditing = (rowId: string | number) => (editonly ? true : editingRows.has(rowId));

    const renderCustom = (
      row: Row,
      column: Column,
      value: any,
      fieldId: string,
      canEdit: boolean,
      isDisabled = false,
    ) => {
      if (!column.component) return null;

      const CustomComponent = column.component;

      const resolvedProps =
        typeof column.componentProps === 'function' ? column.componentProps(row, value) : column.componentProps || {};

      const safeValue =
        column.deriveValue?.(row, { ...(context || {}), getAllRows: () => data } as EditorContext) ??
        value ??
        resolvedProps?.defaultValue ??
        null;

      const handleCustomChange = (name: string, value: any) => {
        const currentValue =
          column.deriveValue?.(row, { ...(context || {}), getAllRows: () => data }) ?? row[column.id] ?? {};

        if (typeof currentValue === 'object' && currentValue !== null) {
          handleCellChange(row.id, column.id, { ...currentValue, [name]: value });
        } else {
          // fallback: treat as primitive
          handleCellChange(row.id, column.id, value);
        }
      };

      const commonProps = {
        id: fieldId,
        name: fieldId,
        value: safeValue,
        row,
        onChange: handleCustomChange,
        canEdit,
        isDisabled,
        ...resolvedProps,
      };

      return <CustomComponent {...commonProps} />;
    };

    const renderCell = (row: Row, column: Column, key: string, context?: Context, isDisabled?: boolean) => {
      const value = column.deriveValue ? column.deriveValue(row, context) : row[column.id];
      const enhancedContext = { ...(context || {}), getAllRows: () => data } as EditorContext;
      const canEdit =
        !readonly &&
        isRowEditing(row.id) &&
        column.type !== 'readonly' &&
        column.type !== 'disabled' &&
        (typeof column.isEditable === 'function' ? column.isEditable(row, context) : (column.isEditable ?? true));

      // render custom component if specified
      if (column.component && column.type === 'custom') {
        return <>{renderCustom(row, column, value, key, canEdit, isDisabled)}</>;
      }

      if (canEdit) {
        const editor = column.renderEditor ?? getDefaultRenderEditor(column.type, column);
        if (editor) {
          return (
            <>
              {editor(value, row, (val: any) => handleCellChange(row.id, column.id, val), enhancedContext, isDisabled)}
            </>
          );
        }
      } else {
        const renderer = column.renderReadOnly ?? getDefaultRenderReadOnly(column.type, column);
        return <>{renderer(value, row, enhancedContext)}</>;
      }
    };

    const className = classNames('table-view', {
      readonly,
      bordered,
      compact: compactView,
    });

    const visibleColumns = columns.filter((column) =>
      typeof column.showColumn === 'function'
        ? column.showColumn({ ...(context || {}), getAllRows: () => data })
        : (column.showColumn ?? true),
    );

    const showActions = !readonly;

    let colCount = visibleColumns.length;
    if (canSelect) colCount += 1;
    if (showActions) colCount += 1;

    const rowCount = data?.length;
    const showLoadMorePagination =
      rowCount > 0 && allowPagination && rowCount < totalCount && paginationType === 'loadmore';
    const showPagePagination = allowPagination && paginationType === 'pages' && totalCount > 0;
    const totalPages = Math.ceil(totalCount / pageSize);

    return (
      <div className={className}>
        {showPagePagination && onPageChange && (
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSizeOptions={pageSizeOptions}
            showCount={showCount}
            showPageSizeOptions={showPageSizeOptions}
            isLoading={isLoading}
          />
        )}
        <div className="table-scroll">
          {isLoading && paginationType === 'pages' && <div className="table-loading-overlay" />}
          <ReactstrapTable striped={striped} responsive size="sm" id={id} data-cy={`${id}-table`}>
            <TableHeader
              showSelect={canSelect}
              columns={visibleColumns}
              showActions={showActions}
              allowSort={allowSort}
              sortState={sortState}
              onSort={handleSort}
              data={data}
              selectedIds={selectedIds}
              handleSelectRow={handleSelectRow}
              isRowDisabled={isRowDisabled}
            />
            <tbody>
              {data?.map((row, rowIndex) => {
                const key = row.id ?? `row-${rowIndex}`;

                // Custom rows render their own <tr> spanning the table (e.g. the ExportDialog preview separator)
                if (row.isCustom && row.getCustomRow) {
                  return row.getCustomRow({ id: key, colCount, rowCount: data.length });
                }

                const isEditing = isRowEditing(row.id);
                const isSelected = selectedIds.includes(String(row.id));

                let isDisabled = false;
                if (typeof isRowDisabled !== 'undefined') {
                  isDisabled = typeof isRowDisabled === 'function' ? isRowDisabled(row) : isRowDisabled;
                }

                const className = classNames({
                  read: !isEditing,
                  edit: isEditing,
                  selected: isSelected,
                  disabled: isDisabled,
                });

                let canDelete = true;
                if (typeof canDeleteRow !== 'undefined') {
                  canDelete = typeof canDeleteRow === 'function' ? canDeleteRow(row) : canDeleteRow;
                }

                return (
                  <tr key={key} className={className} data-id={row.id}>
                    {canSelect && (
                      <td className="select-cell">
                        <Input
                          type="checkbox"
                          name={`${row.id}-checkbox`}
                          id={`${row.id}-checkbox`}
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => handleSelectRow([row.id as string])}
                        />
                      </td>
                    )}
                    {visibleColumns.map((column) => {
                      const key = `${rowIndex}.${column.id}`;
                      const isInvalid = Array.from(invalidCells).some(
                        (prefix) => prefix === key || prefix.startsWith(`${key}.`),
                      );
                      const className = classNames([column.type, column?.classNames?.column], {
                        'invalid-cell': isInvalid,
                      });

                      return (
                        <td key={column.id} className={className}>
                          {renderCell(row, column, key, context, isDisabled)}
                        </td>
                      );
                    })}
                    {!readonly && (
                      <td className="actions-cell">
                        <div className="actions-container">
                          <EditAction
                            row={row}
                            onEdit={() => toggleEditRow?.(row.id)}
                            editonly={editonly}
                            onSaveRow={onSaveRow}
                            isEditing={isEditing}
                          />
                          {canDelete && <DeleteAction row={row} onDelete={() => handleDeleteRow?.(row.id)} />}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {isLoading && <TableLoading colSpan={colCount} />}
              {!isLoading && data?.length === 0 && (
                <TableEmpty colSpan={colCount} icon={emptyIcon} message={emptyMessage} />
              )}
            </tbody>
            <TableFooter
              id={id}
              colSpan={colCount}
              canAdd={showAddRow && !readonly}
              showPagination={showLoadMorePagination}
              onAddRow={handleAddRow}
              onLoadMore={onLoadMore}
            />
          </ReactstrapTable>
        </div>
      </div>
    );
  },
);

TableView.displayName = 'TableView';

export default TableView;
